const isAnchorEl = (el) => el?.tagName === 'A';
const normalizeStateCode = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase();

function resolveTrack(track) {
  if (typeof track === 'function') return track;
  const globalTrack = globalThis.unlk?.sendTrack;
  return typeof globalTrack === 'function' ? globalTrack : () => {};
}

function normalizeActiveStates(input) {
  if (Array.isArray(input)) return input.map(normalizeStateCode).filter(Boolean);

  if (typeof input === 'string') {
    return input.split(',').map(normalizeStateCode).filter(Boolean);
  }

  return [];
}

function mergeConfig(userConfig = {}) {
  const defaults = {
    smartyKey: globalThis.unlk?.smartyKey,
    selectors: null,

    labels: {
      buttonIdle: undefined,
      noMatches: 'We couldn’t find that address. Check it and try again.',
      invalidAddress: 'Enter a valid address',
      selectAddress: 'Select an address from the suggestions',
      lookupError: 'Address lookup is temporarily unavailable. Please try again.',
      unsupportedState: 'State not currently supported'
    },

    debounceMs: 250,
    invalidResetMs: 10_000,

    activeStates: globalThis.unlk?.activeStates || [],
    smartyQualUrl: globalThis.unlk?.smartyQual,

    navigation: {
      mode: 'redirect',
      cta: { dataAttr: 'href' }
    },

    buttonLabelMode: 'never',
    track: null,
    onAddress: null
  };

  const merged = {
    ...defaults,
    ...userConfig,
    selectors: userConfig.selectors ?? defaults.selectors,
    labels: { ...defaults.labels, ...userConfig.labels },
    navigation: {
      ...defaults.navigation,
      ...userConfig.navigation,
      cta: { ...defaults.navigation.cta, ...userConfig.navigation?.cta }
    }
  };

  merged.activeStates = normalizeActiveStates(merged.activeStates);

  return merged;
}

export function smartyLookup({
  smartyKey,
  license = 'us-autocomplete-pro-cloud',
  source = 'all',
  endpoint = 'https://us-autocomplete-pro.api.smarty.com/lookup',
  fetchImpl = globalThis.fetch.bind(globalThis)
} = {}) {
  let controller = null;

  async function lookup({ searchValue, selected = '', qid = 0 }) {
    if (!smartyKey) throw new Error('Smarty key missing');
    if (!searchValue) return { suggestions: [] };

    if (controller) controller.abort();
    controller = new AbortController();

    const params = new URLSearchParams({
      key: smartyKey,
      search: searchValue,
      selected,
      license,
      source
    });

    const resp = await fetchImpl(`${endpoint}?${params}`, { signal: controller.signal });
    const data = await resp.json();

    return { data, qid };
  }

  return { lookup };
}

function highlightMatch(text, query) {
  const frag = document.createDocumentFragment();
  if (!query) {
    frag.append(document.createTextNode(text));
    return frag;
  }

  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    frag.append(document.createTextNode(text));
    return frag;
  }

  if (idx > 0) frag.append(document.createTextNode(text.slice(0, idx)));
  const match = document.createElement('b');
  match.textContent = text.slice(idx, idx + query.length);
  frag.append(match);
  if (idx + query.length < text.length) {
    frag.append(document.createTextNode(text.slice(idx + query.length)));
  }

  return frag;
}

export function formatSuggestions({
  suggestionBox,
  suggestions,
  selected = '',
  query = '',
  onPickAddress,
  onDrilldown,
  onBack = null,
  isDrilldown = false,
  drilldownLabel = '',
  setBusy,
  setActive,
  getSelectedText,
  state = {}
} = {}) {
  if (!suggestionBox) return { currentSuggestions: [], resetIndex() {} };

  suggestionBox.innerHTML = '';
  state.currentSuggestions = [];
  state.currentIndex = -1;

  const fragment = document.createDocumentFragment();

  if (isDrilldown && typeof onBack === 'function') {
    // Header row: pill Back button + address label
    const headerEl = document.createElement('li');
    headerEl.className = 'autocomplete-drilldown-header';

    const backPill = document.createElement('button');
    backPill.type = 'button';
    backPill.className = 'btn btn-sm btn-outline-action';
    backPill.setAttribute('aria-label', 'Back to previous results');

    const backChevron = document.createElement('i');
    backChevron.className = 'fa-regular fa-chevron-left';
    backChevron.setAttribute('aria-hidden', 'true');

    const backText = document.createElement('span');
    backText.textContent = 'Back';
    backPill.append(backChevron, backText);

    const addressSpan = document.createElement('span');
    addressSpan.className = 'autocomplete-drilldown-address';
    addressSpan.textContent = drilldownLabel;

    headerEl.append(backPill, addressSpan);
    backPill.addEventListener('mousedown', (e) => e.preventDefault());
    backPill.addEventListener('click', onBack);
    fragment.append(headerEl);

    // "SELECT YOUR UNIT" section label — not interactive
    const unitLabel = document.createElement('li');
    unitLabel.className = 'unit-label';
    unitLabel.textContent = 'Select your unit';
    unitLabel.setAttribute('aria-hidden', 'true');
    fragment.append(unitLabel);
  }

  for (const sug of suggestions) {
    // eslint-disable-next-line camelcase
    const { street_line, city, state: st, zipcode, secondary, entries } = sug;
    const hasSecondaryData = secondary && entries > 1;

    const el = document.createElement('li');
    el.className = 'btn btn-link-action';

    if (hasSecondaryData) {
      // Multi-entry: two-line layout with building icon and chevron
      el.classList.add('multi-entry');
      el.setAttribute(
        'aria-label',
        // eslint-disable-next-line camelcase
        `${street_line}, ${city}, ${st}, ${zipcode} — ${entries} units, tap to select`
      );
      el.setAttribute('aria-expanded', 'false');

      const textWrap = document.createElement('span');
      textWrap.className = 'suggestion-text';

      const primarySpan = document.createElement('span');
      primarySpan.className = 'suggestion-primary';
      // eslint-disable-next-line camelcase
      primarySpan.append(highlightMatch(`${street_line}, ${city}, ${st}, ${zipcode}`, query));

      const secondarySpan = document.createElement('span');
      secondarySpan.className = 'suggestion-secondary';

      const buildingIcon = document.createElement('i');
      buildingIcon.className = 'fa-regular fa-building';
      buildingIcon.setAttribute('aria-hidden', 'true');

      const secondaryText = document.createTextNode(` ${entries} units — tap to select your unit`);
      secondarySpan.append(buildingIcon, secondaryText);

      textWrap.append(primarySpan, secondarySpan);

      const chevron = document.createElement('i');
      chevron.className = 'fa-regular fa-chevron-right';
      chevron.setAttribute('aria-hidden', 'true');

      el.append(textWrap, chevron);
    } else if (isDrilldown && secondary) {
      // Drilldown unit row: show only the unit (e.g. "Apt 101")
      el.classList.add('unit-row');
      el.textContent = secondary;
    } else {
      const secondaryPart = secondary || '';
      // eslint-disable-next-line camelcase
      const fullText = `${street_line}, ${secondaryPart} ${city}, ${st}, ${zipcode}`.replaceAll(/\s+/g, ' ');
      el.append(highlightMatch(fullText, query));
    }

    el.addEventListener('mousedown', (e) => e.preventDefault());

    el.addEventListener('click', async () => {
      // eslint-disable-next-line camelcase
      const streetLineWithSecondary = `${street_line} ${secondary || ''}`.trim();
      const currentSelected = typeof getSelectedText === 'function' ? getSelectedText() : selected;

      if (hasSecondaryData && !currentSelected.includes(streetLineWithSecondary)) {
        const newSelected = `${streetLineWithSecondary} (${entries}) ${city}, ${st}, ${zipcode}`;
        // eslint-disable-next-line camelcase
        const label = `${street_line}, ${city}, ${st}, ${zipcode}`;
        if (typeof setBusy === 'function') setBusy(true);
        if (typeof onDrilldown === 'function') {
          await onDrilldown({ streetLineWithSecondary, newSelected, label });
        }

        return;
      }

      if (typeof setActive === 'function') setActive(false);

      if (typeof onPickAddress === 'function') {
        onPickAddress({ streetLineWithSecondary, city, state: st, zipcode });
      }
    });

    // Use indexOf so the index stays correct when a header/label shifts DOM positions
    el.addEventListener('mouseenter', () => {
      for (const node of state.currentSuggestions) node.classList.remove('active');
      state.currentIndex = state.currentSuggestions.indexOf(el);
      el.classList.add('active');
    });

    el.addEventListener('mouseleave', () => {
      state.currentIndex = -1;
      el.classList.remove('active');
      state.currentSuggestions[0]?.classList.add('active');
    });

    fragment.append(el);
  }

  suggestionBox.append(fragment);
  // Only interactive items (.btn) are keyboard-navigable — excludes the unit label
  state.currentSuggestions = Array.from(suggestionBox.querySelectorAll('li.btn'));
  // The CTA uses the first result by default, so make that choice visible before it is submitted.
  state.currentSuggestions[0]?.classList.add('active');

  return {
    currentSuggestions: state.currentSuggestions,
    resetIndex() {
      state.currentIndex = -1;
      for (const node of state.currentSuggestions) node.classList.remove('active');
    }
  };
}

export function initSmarty(userConfig = {}) {
  const config = mergeConfig(userConfig);
  const sendTrack = resolveTrack(config.track);
  const requiredSelectorKeys = [
    'addressInput',
    'inputGroup',
    'suggestionBox',
    'qualButton',
    'invalidScope'
  ];

  if (!config.selectors) return false;

  const selectorsOk = requiredSelectorKeys.every((k) => {
    const v = config.selectors?.[k];
    return typeof v === 'string' && v.trim();
  });
  if (!selectorsOk) return false;

  const addressInput = document.querySelector(config.selectors.addressInput);
  const inputGroup = document.querySelector(config.selectors.inputGroup);
  const suggestionBox = document.querySelector(config.selectors.suggestionBox);
  const qualButton = document.querySelector(config.selectors.qualButton);

  if (!addressInput || !inputGroup || !suggestionBox || !qualButton) return false;

  if (!config.smartyKey) return false;
  if (!config.smartyQualUrl) return false;

  const initialButtonHtml = qualButton.innerHTML;
  let lastNonSpinnerButtonHtml = initialButtonHtml;

  let debounceTimeout;
  let invalidResetTimeout = null;

  const smartyState = { currentIndex: -1, currentSuggestions: [] };
  let lastQueryId = 0;
  let lookupStatus = 'idle';
  let advanceAfterLookup = false;

  // Stores the previous search state so the Back button can restore it
  let drilldownPrev = null;

  const { lookup } = smartyLookup({ smartyKey: config.smartyKey });

  const CTA_DATA_ATTR = config.navigation?.cta?.dataAttr || 'href';

  function getStoredCtaUrl() {
    if (isAnchorEl(qualButton)) return qualButton.getAttribute('href') || '';
    return qualButton.dataset?.[CTA_DATA_ATTR] || '';
  }

  function setStoredCtaUrl(url) {
    if (!url) return;
    if (isAnchorEl(qualButton)) {
      qualButton.setAttribute('href', url);
      return;
    }

    if (qualButton.dataset) qualButton.dataset[CTA_DATA_ATTR] = url;
  }

  function clearStoredCtaUrl() {
    if (isAnchorEl(qualButton)) qualButton.removeAttribute('href');
    else if (qualButton.dataset) delete qualButton.dataset[CTA_DATA_ATTR];
  }

  function buildUrl(base, params = {}) {
    const b = String(base ?? '').trim();
    if (!b) return '';

    const u = new URL(b, globalThis.location.origin);

    for (const [k, v] of Object.entries(params)) {
      const sv = String(v ?? '').trim();
      if (!sv) continue;
      u.searchParams.set(k, sv);
    }

    return u.toString();
  }

  function hasIdleOverride() {
    return (
      typeof config.labels?.buttonIdle === 'string' && config.labels.buttonIdle.trim().length > 0
    );
  }

  function shouldForceLabelFor(reason) {
    if (!hasIdleOverride()) return false;
    const mode = config.buttonLabelMode;
    return mode === 'both' || mode === reason;
  }

  function setSuggestionState({ active, busy } = {}) {
    if (typeof active === 'boolean') {
      suggestionBox.classList.toggle('active', active);
      suggestionBox.classList.toggle('inactive', !active);
      if (!active) suggestionBox.setAttribute('aria-busy', 'false');
    }

    if (typeof busy === 'boolean') {
      suggestionBox.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  function setButtonIdle({ forceLabel = false } = {}) {
    if (forceLabel && hasIdleOverride()) {
      qualButton.innerHTML = config.labels.buttonIdle;
      lastNonSpinnerButtonHtml = qualButton.innerHTML;
    } else if (qualButton.querySelector?.('.spinner-border')) {
      qualButton.innerHTML = lastNonSpinnerButtonHtml || initialButtonHtml;
    }

    qualButton.disabled = false;
  }

  function setButtonLoading() {
    lastNonSpinnerButtonHtml = qualButton.innerHTML;
    qualButton.innerHTML =
      '<div class="spinner-border text-light" role="status"><span class="visually-hidden">Loading...</span></div>';
    qualButton.disabled = true;
  }

  function restoreOriginalButtonHtml() {
    qualButton.innerHTML = initialButtonHtml;
    lastNonSpinnerButtonHtml = initialButtonHtml;
    qualButton.disabled = false;
  }

  function goOrSetCta(url) {
    let next = String(url ?? '').trim();
    if (!next) return;

    if (next.startsWith('?')) {
      const baseForHeal = String(config.smartyQualUrl || '').trim() || globalThis.location.href;

      try {
        const healed = new URL(baseForHeal);
        const incoming = new URLSearchParams(next.slice(1));
        for (const [k, v] of incoming.entries()) healed.searchParams.set(k, v);
        next = healed.toString();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Smarty] Failed to heal query-only CTA URL:', error);
        return;
      }
    }

    if (config.navigation?.mode === 'cta') {
      setStoredCtaUrl(next);
      // eslint-disable-next-line no-console
      console.log(
        '[Smarty] stored CTA URL:',
        isAnchorEl(qualButton)
          ? qualButton.getAttribute('href')
          : qualButton.dataset?.[CTA_DATA_ATTR]
      );
      setButtonIdle({ forceLabel: shouldForceLabelFor('cta') });
      return;
    }

    globalThis.location.assign(next);
  }

  const invalidClass = 'address-invalid';

  function getExistingInvalidEl() {
    return inputGroup.querySelector(`.${invalidClass}`);
  }

  function setInvalidMessage(htmlMessage) {
    addressInput.classList.add('is-invalid');
    addressInput.parentElement?.classList.add('is-invalid');
    inputGroup.classList.add('has-validation');

    let invalid = getExistingInvalidEl();
    if (!invalid) {
      invalid = document.createElement('div');
      invalid.className = `invalid-feedback ${invalidClass}`;
      inputGroup.append(invalid);
    }

    invalid.innerHTML = htmlMessage;
  }

  function clearInvalidUI() {
    addressInput.classList.remove('is-invalid');
    addressInput.parentElement?.classList.remove('is-invalid');
    inputGroup.classList.remove('has-validation');

    const existing = getExistingInvalidEl();
    if (existing) existing.remove();
  }

  function clearSuggestionsUI() {
    drilldownPrev = null;
    suggestionBox.innerHTML = '';
    setSuggestionState({ active: false });
    addressInput.classList.remove('suggestion-showing');
    qualButton.classList.remove('suggestion-showing');
    smartyState.currentIndex = -1;
    smartyState.currentSuggestions = [];
  }

  function showSubmitError(message) {
    clearSuggestionsUI();
    setInvalidMessage(message);
  }

  function queueAdvanceAfterLookup() {
    advanceAfterLookup = true;
    setButtonLoading();
  }

  function consumeAdvanceAfterLookup(onAdvance) {
    if (!advanceAfterLookup) return;

    advanceAfterLookup = false;
    setButtonIdle();
    onAdvance();
  }

  globalThis.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;

    setButtonIdle({ forceLabel: shouldForceLabelFor('reset') });

    addressInput.value = '';
    clearSuggestionsUI();
    clearInvalidUI();

    if (config.navigation?.mode === 'cta') clearStoredCtaUrl();
  });

  async function sendLookupRequest(searchValue, selected = '', qid = 0, isDrilldown = false) {
    if (!searchValue) return;

    lookupStatus = 'loading';

    try {
      const { data } = await lookup({ searchValue, selected, qid });
      if (qid !== lastQueryId) return;

      setSuggestionState({ busy: false });

      if (data?.suggestions?.length) {
        lookupStatus = 'matches';
        const prevSearch = drilldownPrev ? { ...drilldownPrev } : null;

        formatSuggestions({
          suggestionBox,
          suggestions: data.suggestions,
          selected,
          query: isDrilldown ? '' : searchValue,
          isDrilldown,
          drilldownLabel: prevSearch?.label ?? '',
          onBack:
            isDrilldown && prevSearch
              ? async () => {
                  drilldownPrev = null;
                  setSuggestionState({ busy: true });
                  await sendLookupRequest(prevSearch.searchValue, prevSearch.selected, ++lastQueryId, false);
                }
              : null,
          setBusy: (v) => setSuggestionState({ busy: v }),
          setActive: (v) => setSuggestionState({ active: v }),
          state: smartyState,
          getSelectedText: () => selected,

          async onDrilldown({ streetLineWithSecondary, newSelected, label }) {
            drilldownPrev = { searchValue, selected, label };
            setSuggestionState({ busy: true });
            await sendLookupRequest(streetLineWithSecondary, newSelected, ++lastQueryId, true);
          },

          onPickAddress({ streetLineWithSecondary, city, state, zipcode }) {
            setSuggestionState({ active: false });
            clearInvalidUI();
            populateInput({ streetLineWithSecondary, city, state, zipcode });
          }
        });

        consumeAdvanceAfterLookup(() => {
          const firstSuggestion = smartyState.currentSuggestions[0];
          if (firstSuggestion) firstSuggestion.click();
        });
      } else {
        lookupStatus = 'no-matches';
        suggestionBox.innerHTML = '';
        const li = document.createElement('li');
        li.textContent = config.labels.noMatches;
        suggestionBox.append(li);
        smartyState.currentSuggestions = Array.from(suggestionBox.querySelectorAll('li.btn'));
        smartyState.currentIndex = -1;

        consumeAdvanceAfterLookup(() => showSubmitError(config.labels.noMatches));
      }
    } catch (error) {
      if (qid !== lastQueryId) return;

      if (error?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error(error);
        lookupStatus = 'error';
        consumeAdvanceAfterLookup(() => showSubmitError(config.labels.lookupError));
      }

      setSuggestionState({ busy: false });
    }
  }

  function startLookup(searchValue) {
    clearTimeout(debounceTimeout);
    drilldownPrev = null;
    smartyState.currentIndex = -1;

    setSuggestionState({ active: true, busy: true });
    addressInput.classList.add('suggestion-showing');
    qualButton.classList.add('suggestion-showing');

    const qid = ++lastQueryId;
    // eslint-disable-next-line no-void
    void sendLookupRequest(searchValue, '', qid);
  }

  addressInput.addEventListener('keydown', (e) => {
    const { key } = e;

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      if (!smartyState.currentSuggestions.length) return;

      for (const el of smartyState.currentSuggestions) {
        el.classList.remove('active');
      }

      smartyState.currentIndex =
        key === 'ArrowDown'
          ? (smartyState.currentIndex + 1) % smartyState.currentSuggestions.length
          : (smartyState.currentIndex - 1 + smartyState.currentSuggestions.length) %
            smartyState.currentSuggestions.length;

      const el = smartyState.currentSuggestions[smartyState.currentIndex];

      if (el) {
        el.classList.add('active');
        el.scrollIntoView({ block: 'nearest' });
      }

      return;
    }

    if (key === 'Enter') {
      const suggestionsBusy = suggestionBox.getAttribute('aria-busy') === 'true';

      const targetSuggestion =
        smartyState.currentIndex >= 0
          ? smartyState.currentSuggestions[smartyState.currentIndex]
          : smartyState.currentSuggestions[0];

      if (targetSuggestion && !suggestionsBusy) {
        e.preventDefault();
        targetSuggestion.click();
      }

      return;
    }

    if (key === 'Escape') {
      clearSuggestionsUI();
    }
  });

  addressInput.addEventListener('input', () => {
    clearTimeout(invalidResetTimeout);
    invalidResetTimeout = null;

    setButtonIdle();
    clearInvalidUI();
    advanceAfterLookup = false;
    lookupStatus = 'scheduled';
    lastQueryId += 1;

    if (config.navigation?.mode === 'cta') {
      clearStoredCtaUrl();
    }

    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(() => {
      const searchValue = addressInput.value.trim();

      drilldownPrev = null;
      smartyState.currentIndex = -1;

      if (!searchValue) {
        lookupStatus = 'idle';
        clearSuggestionsUI();
        clearInvalidUI();
        return;
      }

      startLookup(searchValue);
    }, config.debounceMs);
  });

  qualButton.addEventListener('click', (e) => {
    if (config.navigation?.mode === 'cta') {
      const stored = getStoredCtaUrl();
      if (stored) {
        if (!isAnchorEl(qualButton)) globalThis.location.assign(stored);
        return;
      }

      if (isAnchorEl(qualButton)) e.preventDefault();
    }

    const firstSuggestion = smartyState.currentSuggestions[0];
    const suggestionsActive = suggestionBox.classList.contains('active');
    const suggestionsBusy = suggestionBox.getAttribute('aria-busy') === 'true';

    if (firstSuggestion && suggestionsActive && !suggestionsBusy) {
      e.preventDefault();
      firstSuggestion.click();
      return;
    }

    const searchValue = addressInput.value.trim();
    if (searchValue) {
      e.preventDefault();

      if (lookupStatus === 'scheduled' || lookupStatus === 'idle') {
        queueAdvanceAfterLookup();
        startLookup(searchValue);
        return;
      }

      if (lookupStatus === 'loading') {
        queueAdvanceAfterLookup();
        return;
      }

      const message =
        lookupStatus === 'error'
          ? config.labels.lookupError
          : lookupStatus === 'no-matches'
            ? config.labels.noMatches
            : config.labels.selectAddress;
      showSubmitError(message);
      return;
    }

    const existing = getExistingInvalidEl();
    if (existing) {
      existing.innerHTML = config.labels.invalidAddress;
      return;
    }

    setInvalidMessage(config.labels.invalidAddress);
  });

  function populateInput({ streetLineWithSecondary, city, state, zipcode }) {
    if (!streetLineWithSecondary || !city || !state || !zipcode) return;
    addressInput.value = `${streetLineWithSecondary}, ${city}, ${state}`;
    loadAddress({ streetLineWithSecondary, city, state, zipcode });
  }

  function loadAddress({ streetLineWithSecondary, city, state, zipcode }) {
    addressInput.classList.remove('suggestion-showing');
    qualButton.classList.remove('suggestion-showing');
    setButtonLoading();

    const stateCode = String(state || '')
      .trim()
      .toUpperCase();
    const isSupported = config.activeStates.includes(stateCode);

    const baseParams = {
      streetAddress: streetLineWithSecondary,
      city,
      state: stateCode,
      zipcode
    };

    sendTrack('Property Inquiry Made', {
      street: streetLineWithSecondary,
      city,
      state: stateCode,
      // eslint-disable-next-line camelcase
      zip_code: zipcode,
    });

    if (!isSupported) {
      setInvalidMessage(config.labels.unsupportedState);
      setButtonIdle();

      clearTimeout(invalidResetTimeout);
      invalidResetTimeout = setTimeout(() => {
        addressInput.value = '';
        addressInput.classList.remove('suggestion', 'is-invalid');

        restoreOriginalButtonHtml();
        clearInvalidUI();
        clearSuggestionsUI();

        if (config.navigation?.mode === 'cta') clearStoredCtaUrl();

        invalidResetTimeout = null;
      }, config.invalidResetMs);

      return;
    }

    localStorage.setItem(
      'address',
      JSON.stringify(`${streetLineWithSecondary}, ${city}, ${stateCode}, ${zipcode}`)
    );

    if (typeof config.onAddress === 'function') {
      config.onAddress({ streetLineWithSecondary, city, state: stateCode, zipcode });
    }

    goOrSetCta(buildUrl(config.smartyQualUrl, baseParams));
  }

  return true;
}

if (typeof globalThis !== 'undefined') {
  globalThis.unlk = globalThis.unlk || {};
  globalThis.unlk.smartyLookup = smartyLookup;
  globalThis.unlk.formatSuggestions = formatSuggestions;
  globalThis.unlk.smarty = globalThis.unlk.smarty || {};
  globalThis.unlk.smarty.init = initSmarty;
}
